/**
 * CDR reporting API (brief §5 Phase 2).
 *
 * The unified, consolidated report across all nodes: filtered/paginated
 * listing, per-call detail (legs + stage timeline + recordings), CSV export,
 * and recording playback/download URLs. Export and audio are same-origin
 * GETs, so the session cookie authorizes them without extra plumbing —
 * the URLs here feed `<a href>` / `<audio src>` directly.
 */

import { apiFetch } from "./client";

/** One row of the unified CDR report. */
export interface CdrRow {
  id: number;
  source_node_id: number;
  vodia_call_id: string | null;
  start_time: string | null;
  end_time: string | null;
  direction: string | null;
  from_uri: string | null;
  to_uri: string | null;
  extension: string | null;
  trunk: string | null;
  duration_seconds: number | null;
  disposition: string | null;
  has_recording: boolean;
}

/** Filters accepted by the report, export, and their query strings. */
export interface CdrFilters {
  node_id?: number;
  direction?: string;
  extension?: string;
  search?: string;
  start_after?: string;
  start_before?: string;
}

export interface CdrPage {
  items: CdrRow[];
  total: number;
  limit: number;
  offset: number;
}

/** One SIP leg of a call (detail view). */
export interface CdrLeg {
  kind: string | null;
  direction: string | null;
  from_uri: string | null;
  to_uri: string | null;
  local_party: string | null;
  remote_party: string | null;
  extension: string | null;
  extension_name: string | null;
  trunk_name: string | null;
  codec: string | null;
  mos: number | null;
  code: string | null;
  start_time: string | null;
  connect_time: string | null;
  end_time: string | null;
  redirect: string | null;
  quality: string | null;
}

/** One stage of the call timeline (AA -> queue -> agent), with durations. */
export interface CdrState {
  seq: number;
  type: string | null;
  account: string | null;
  account_name: string | null;
  answered_extension: string | null;
  answered_extension_name: string | null;
  reason: string | null;
  routing_digits: string | null;
  start_time: string | null;
  duration_ring_ms: number | null;
  duration_talk_ms: number | null;
  duration_hold_ms: number | null;
  duration_idle_ms: number | null;
  duration_ivr_ms: number | null;
}

/** A recording pointer; playable only once `available`. */
export interface CdrRecording {
  id: number;
  recorded_at: string | null;
  duration_seconds: number | null;
  from_party: string | null;
  to_party: string | null;
  account: string | null;
  account_name: string | null;
  extension: string | null;
  extension_name: string | null;
  size_bytes: number | null;
  available: boolean;
  fetch_error: string | null;
}

export interface CdrDetail extends CdrRow {
  legs: CdrLeg[];
  states: CdrState[];
  recordings: CdrRecording[];
}

/** Build the query string shared by the report and the CSV export. */
export function cdrQueryString(filters: CdrFilters, limit?: number, offset?: number): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  if (limit !== undefined) params.set("limit", String(limit));
  if (offset !== undefined) params.set("offset", String(offset));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Fetch a filtered, paginated page of the unified report. */
export function getCdrs(filters: CdrFilters, limit = 50, offset = 0): Promise<CdrPage> {
  return apiFetch<CdrPage>(`/api/cdr${cdrQueryString(filters, limit, offset)}`);
}

/** Fetch one call's detail: legs, stage timeline, recordings. */
export function getCdrDetail(cdrId: number): Promise<CdrDetail> {
  return apiFetch<CdrDetail>(`/api/cdr/${cdrId}`);
}

/** URL for the CSV export of the current filters (`cdr.export`). */
export function cdrExportUrl(filters: CdrFilters): string {
  return `/api/cdr/export.csv${cdrQueryString(filters)}`;
}

/** URL that streams a recording for in-browser playback (`recordings.play`). */
export function recordingPlayUrl(recordingId: number): string {
  return `/api/cdr/recordings/${recordingId}`;
}

/** URL that downloads a recording as an attachment (`recordings.download`). */
export function recordingDownloadUrl(recordingId: number): string {
  return `/api/cdr/recordings/${recordingId}/download`;
}
